// ============================================================================
// Manage / cancel a monthly recurring donation.
// Reached from the receipt-email link or the result page as /recurring?t=<token>.
// The manage token is the donor's credential: it fetches the subscription and
// authorises cancellation (Stripe SI removal + DynamoDB status + SES email),
// all via the public recurring endpoints.
// ============================================================================
import { useEffect, useState } from "react";
import { Alert, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { useAws } from "../aws";
import { displayProgramName } from "../misc/recurringDemo";

const fmtDate = (ms) => {
  if (!ms) return "—";
  try {
    return format(new Date(Number(ms)), "yyyy-MM-dd");
  } catch (e) {
    return "—";
  }
};

const Row = ({ label, value }) => (
  <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid #eee", fontSize: "14px" }}>
    <span style={{ color: "#666" }}>{label}</span>
    <span style={{ fontWeight: 600, textAlign: "right" }}>{value}</span>
  </div>
);

const RecurringManagePage = () => {
  const navigate = useNavigate();
  const { awsHttp } = useAws();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("t");

  const [loading, setLoading] = useState(true);
  const [sub, setSub] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const loadSub = () => {
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    awsHttp({ HTTPRequestMethod: "GET", APIFunction: "user/subscription", unauthorize: true, queryString: { token }, requestBody: null })
      .then((res) => {
        const body = res?.data?.body;
        setSub(body && typeof body === "object" ? body : null);
      })
      .catch(() => setSub(null))
      .finally(() => setLoading(false));
  };

  useEffect(loadSub, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConfirmCancel = () => {
    if (cancelling) return;
    setCancelling(true);
    awsHttp({ HTTPRequestMethod: "POST", APIFunction: "user/cancel_subscription", unauthorize: true, requestBody: { token } })
      .then((res) => {
        if (res?.data?.statusCode === 200) {
          setDialogOpen(false);
          loadSub();
        } else {
          alert(typeof res?.data?.body === "string" ? res.data.body : "取消時發生錯誤，請重試。");
        }
      })
      .catch(() => alert("取消時發生錯誤，請重試。"))
      .finally(() => setCancelling(false));
  };

  if (loading) {
    return (
      <div className="page-view event-detail" style={{ textAlign: "center", paddingTop: "80px" }}>
        <CircularProgress />
      </div>
    );
  }

  if (!token || !sub) {
    return (
      <div className="page-view event-detail">
        <img className="logo mt-2 mb-6" src="/logoPNB.png" alt="pause-and-breathe-logo" />
        <div className={`card-view donation-card donation-result-card`} style={{ marginBottom: "40px" }}>
          <h1 className="mb-1">找不到每月捐款紀錄</h1>
          <h4 style={{ color: "#666" }}>
            此連結無效或已過期。如需協助，請電郵至
            <a href="mailto:hello@pausebreathe.org"> hello@pausebreathe.org</a>。
          </h4>
        </div>
      </div>
    );
  }

  const cancelled = sub.recurring_status === "cancelled";
  const statusChip = {
    active: { label: "生效中", color: "success" },
    pending: { label: "待確認", color: "warning" },
    past_due: { label: "扣款失敗", color: "error" },
    cancelled: { label: "已取消", color: "default" },
  }[sub.recurring_status] || { label: sub.recurring_status || "—", color: "default" };

  return (
    <div className="page-view event-detail">
      <img className="logo mt-2 mb-6" src="/logoPNB.png" alt="pause-and-breathe-logo" />
      <div className={`card-view donation-card donation-result-card`} style={{ marginBottom: "40px" }}>
        <h1 className="mb-1">管理每月定期捐款</h1>
        {sub.donor_name ? <h4 style={{ color: "#666" }}>捐款者：{sub.donor_name}</h4> : null}

        {cancelled ? (
          <Alert severity="success" sx={{ margin: "16px 0" }}>
            您的每月捐款已成功取消。我們已移除您的每月定期扣款安排，取消確認電郵已發送至您的電郵。
            感謝您一直以來對「停一停 心呼吸」的支持！
          </Alert>
        ) : null}

        <div
          style={{
            background: "#f7fbff",
            border: "1px solid #bfddf1",
            borderRadius: "8px",
            padding: "8px 16px",
            margin: "16px 0",
          }}
        >
          <Row
            label="狀態"
            value={<Chip label={statusChip.label} color={statusChip.color} size="small" sx={statusChip.color === "success" ? { color: "#fff" } : {}} />}
          />
          <Row label="每月捐款金額" value={`港幣 $${Number(sub.amount || 0).toLocaleString()}／月`} />
          <Row label="捐款支持計劃" value={displayProgramName(sub.program_name)} />
          <Row label="開始日期" value={fmtDate(sub.started_at)} />
          <Row label="下次扣款日期" value={cancelled ? "—（已停止扣款）" : fmtDate(sub.next_charge_at)} />
          <Row label="付款方式" value={sub.card_brand ? `${sub.card_brand.toUpperCase()} •••• ${sub.card_last4}` : "信用卡"} />
          <Row label="已扣款次數" value={`${Number(sub.charge_count || 0)} 次`} />
        </div>

        {!cancelled ? (
          <>
            <div style={{ background: "#fff8e1", border: "1px solid #ffe0a3", borderRadius: 8, padding: "10px 14px", margin: "4px 0 12px" }}>
              <h4 style={{ fontSize: "13px", color: "#8a6d3b" }}>
                如你想變更捐款金額，請先取消目前的每月捐款，並重新設立一個新的每月捐款指示。
              </h4>
            </div>
            <h4 style={{ fontSize: "13px", color: "#888" }}>取消後，將不再從您的信用卡扣款；您過往的捐款及收據不受影響。</h4>
            <div style={{ marginTop: "16px" }}>
              <Button variant="outlined" color="error" onClick={() => setDialogOpen(true)}>
                取消每月捐款
              </Button>
            </div>
          </>
        ) : (
          <div style={{ marginTop: "16px" }}>
            <Button variant="outlined" onClick={() => navigate("/donation")}>
              重新設立每月捐款
            </Button>
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onClose={() => !cancelling && setDialogOpen(false)}>
        <DialogTitle>確認取消每月捐款？</DialogTitle>
        <DialogContent>
          <DialogContentText component="div" sx={{ fontSize: "14px" }}>
            取消後：
            <ol style={{ margin: "8px 0 0", paddingLeft: "20px" }}>
              <li>我們將即時移除您的每月定期扣款安排，不再扣款；</li>
              <li>取消確認電郵將發送至您的電郵；</li>
              <li>您過往的捐款紀錄及收據不受影響。</li>
            </ol>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={cancelling}>
            保留每月捐款
          </Button>
          <Button variant="contained" color="error" onClick={handleConfirmCancel} disabled={cancelling}>
            確認取消
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default RecurringManagePage;
